import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  ACTIVITY_ENVIRONMENT_PATTERN,
  ACTIVITY_GUID_PATTERN,
} from "../api/copilot-activity-api.js";
import {
  CopilotMonitorError,
  MONITOR_UTC_PATTERN,
  getCopilotAgentSessionSummaryInputSchema,
  type CopilotMonitorApi,
} from "../api/copilot-monitor-api.js";

export const getCopilotAgentSessionSummaryTool: Tool = {
  name: "get_copilot_agent_session_summary",
  description:
    "EXPERIMENTAL agent-wide Copilot Studio Monitor session aggregates for an explicit current environment, agent and positive UTC interval of at most 31 days \u2014 this is not own-user activity. One bounded silent Power Platform GET after a fixed Dataverse preflight; at most 200 sessions within 1 MiB, a 15-second API deadline, no pagination or retries. Returns a first-response row count, a nullable service-reported total with availability state, and counts for closed channel/outcome/orchestration labels plus unknown/missing/null/invalid buckets \u2014 no session IDs, conversation IDs, text, outcome reasons, user IDs or timestamps. Observed outcome labels do not prove ticket resolution or runtime success, and equal row/total counts do not prove completeness.",
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      environment: {
        type: "string",
        pattern: ACTIVITY_ENVIRONMENT_PATTERN.source,
        description:
          "Exact current environment GUID or Default-GUID; no names or alternate environments.",
      },
      agentId: { type: "string", pattern: ACTIVITY_GUID_PATTERN.source },
      utcStartTime: {
        type: "string",
        minLength: 24,
        maxLength: 24,
        pattern: MONITOR_UTC_PATTERN.source,
        description: "Valid canonical UTC timestamp, e.g. 2026-09-01T00:00:00.000Z.",
      },
      utcEndTime: {
        type: "string",
        minLength: 24,
        maxLength: 24,
        pattern: MONITOR_UTC_PATTERN.source,
        description: "Valid canonical UTC timestamp after start, at most 31 days later.",
      },
    },
    required: ["environment", "agentId", "utcStartTime", "utcEndTime"],
  },
};
export async function handleGetCopilotAgentSessionSummary(
  context: { monitorApi: CopilotMonitorApi | null },
  input: unknown,
) {
  const parsed = getCopilotAgentSessionSummaryInputSchema.safeParse(input);
  if (!parsed.success) throw new CopilotMonitorError("input");
  if (!context.monitorApi) throw new CopilotMonitorError("unavailable");
  const result = await context.monitorApi.getSessionSummary(parsed.data);
  return {
    text: `Summarized ${result.sourceRowCount} agent-wide sessions from one response. Service totals and observed outcome labels are not proof of completeness or successful ticket actions. No session details or tenant changes.`,
    structured: { copilotMonitorSessionSummary: result },
  };
}
